import Grid from '@material-ui/core/Grid';
import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Input,
    Button,
    FormControlLabel,
    Checkbox
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import moment from 'moment';
import { useSwaggerClient } from '../../../hooks/useSwaggerClient';

// Helper to extract start time from timeshareTimeslot (handles both old string and new object format)
function getStartFromTimeslot(timeslot) {
    if (!timeslot) {
        return null;
    }
    if (typeof timeslot === 'string') {
        return timeslot;
    }
    return timeslot.start || null;
}

// Helper to extract isCustom from timeshareTimeslot
function getIsCustomFromTimeslot(timeslot) {
    if (!timeslot) {
        return false;
    }
    if (typeof timeslot === 'object') {
        return timeslot.isCustom || false;
    }
    return false;
}

export default function TimeshareTimeslotPicker({
    activityId,
    activityName,
    timeshareTimeslot,
    setTimeshareTimeslot
}) {
    const startTime = getStartFromTimeslot(timeshareTimeslot);
    const [selectedTimeslot, setSelectedTimeslot] = useState('');
    const [selectedDate, setSelectedDate] = useState(
        startTime ? moment(startTime) : moment().add(1, 'day')
    );
    const [currentMonth, setCurrentMonth] = useState(
        startTime
            ? moment(startTime).startOf('month')
            : moment()
                  .add(1, 'day')
                  .startOf('month')
    );
    const [initialized, setInitialized] = useState(false);
    const [isCustomTime, setIsCustomTime] = useState(getIsCustomFromTimeslot(timeshareTimeslot));
    const [customTime, setCustomTime] = useState(
        startTime ? moment(startTime).format('HH:mm:ss') : '09:00:00'
    );
    const [customEndTime, setCustomEndTime] = useState(
        timeshareTimeslot?.end ? moment(timeshareTimeslot.end).format('HH:mm:ss') : '10:00:00'
    );

    // Initialize selectedDate from timeshareTimeslot
    useEffect(() => {
        if (!initialized && startTime) {
            setSelectedDate(moment(startTime));
            setCurrentMonth(moment(startTime).startOf('month'));
            setInitialized(true);
        }
    }, [startTime, initialized]);

    // Fetch timeslots for the entire month to determine available dates
    const [monthlyTimeslots] = useSwaggerClient(
        activityId && currentMonth ? 'activities' : false,
        'getTimeslotsForActivity',
        {
            activityId: activityId,
            fromDate: currentMonth?.format('YYYY-MM-DD'),
            toDate: currentMonth
                ?.clone()
                .endOf('month')
                .format('YYYY-MM-DD'),
            groupSize: 1
        },
        [currentMonth?.format('YYYY-MM'), activityId],
        data => data.timeslots || []
    );

    // Get timeslots for the selected date
    const timeslotsForSelectedDate = useMemo(() => {
        if (!selectedDate) {
            return [];
        }
        const items =
            monthlyTimeslots?.filter(
                e => e.start_time && moment(e.start_time).isSame(selectedDate, 'day')
            ) || [];
        // unique by start_time
        const uniqueItems = Array.from(new Map(items.map(i => [i['start_time'], i])).values());
        const mappedItems = uniqueItems.map(t => ({
            ...t,
            timeslotValue: moment(t.start_time).format() // Use ISO string as value
        }));
        // sort by start_time
        mappedItems.sort((a, b) => moment(a.start_time).diff(moment(b.start_time)));

        return mappedItems;
    }, [monthlyTimeslots, selectedDate]);

    // Initialize selected timeslot based on current timeshareTimeslot
    useEffect(() => {
        if (startTime && !isCustomTime) {
            const currentTimeslot = timeslotsForSelectedDate.find(t =>
                moment(t.start_time).isSame(moment(startTime), 'minute')
            );
            if (currentTimeslot) {
                setSelectedTimeslot(currentTimeslot.timeslotValue);
            }
        }
    }, [startTime, timeslotsForSelectedDate, isCustomTime]);

    // Handle date selection from calendar
    const handleDateChange = newDate => {
        setSelectedDate(moment(newDate));

        // Check if we need to fetch data for a different month
        const newMonth = moment(newDate).startOf('month');
        if (!newMonth.isSame(currentMonth, 'month')) {
            setCurrentMonth(newMonth);
        }

        // Reset timeslot selection
        setSelectedTimeslot('');

        // If custom time mode, update the timeslot with the custom time
        if (isCustomTime) {
            const day = moment(newDate).format('YYYY-MM-DD');
            setTimeshareTimeslot({
                start: `${day} ${customTime}`,
                end: `${day} ${customEndTime}`,
                isCustom: true
            });
        }
    };

    // Handle custom start time change
    const handleCustomTimeChange = event => {
        const newTime = event.target.value;
        setCustomTime(newTime);

        if (selectedDate && newTime) {
            const day = moment(selectedDate).format('YYYY-MM-DD');
            setTimeshareTimeslot({
                start: `${day} ${newTime}`,
                end: `${day} ${customEndTime}`,
                isCustom: true
            });
        }
    };

    // Handle custom end time change
    const handleCustomEndTimeChange = event => {
        const newTime = event.target.value;
        setCustomEndTime(newTime);

        if (selectedDate && newTime) {
            const day = moment(selectedDate).format('YYYY-MM-DD');
            setTimeshareTimeslot({
                start: `${day} ${customTime}`,
                end: `${day} ${newTime}`,
                isCustom: true
            });
        }
    };

    // Handle custom time mode toggle
    const handleCustomTimeToggle = event => {
        setIsCustomTime(event.target.checked);
        if (event.target.checked) {
            // Clear selected timeslot when switching to custom
            setSelectedTimeslot('');
            // Set initial custom time
            if (selectedDate) {
                const day = moment(selectedDate).format('YYYY-MM-DD');
                setTimeshareTimeslot({
                    start: `${day} ${customTime}`,
                    end: `${day} ${customEndTime}`,
                    isCustom: true
                });
            }
        }
    };

    return (
        <>
            <Grid item xs={12}>
                <h4>Timeshare Activity Timeslot</h4>
                {activityName && <small style={{ color: '#666' }}>Activity: {activityName}</small>}
            </Grid>

            <Grid item xs={12}>
                <DatePicker
                    label="Select date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    inputFormat="MM/DD/YYYY"
                    renderInput={params => (
                        <TextField
                            {...params}
                            fullWidth
                            variant="outlined"
                            size="small"
                            helperText=""
                        />
                    )}
                    onMonthChange={newMonth => {
                        if (newMonth) {
                            const monthStart = moment(newMonth).startOf('month');
                            setCurrentMonth(monthStart);
                        }
                    }}
                    shouldDisableDate={date => {
                        // In custom mode, allow all dates
                        if (isCustomTime) {
                            return false;
                        }
                        // Disable dates that do not have any available timeslots
                        const hasTimeslots = monthlyTimeslots?.some(t =>
                            moment(t.start_time).isSame(date, 'day')
                        );
                        return !hasTimeslots;
                    }}
                />
            </Grid>

            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isCustomTime}
                            onChange={handleCustomTimeToggle}
                            size="small"
                        />
                    }
                    label="Use custom time"
                />
            </Grid>

            {!isCustomTime && (
                <Grid item xs={12}>
                    {timeslotsForSelectedDate.length > 0 ? (
                        <FormControl fullWidth variant="outlined" size="small">
                            <InputLabel>Available start times</InputLabel>
                            <Select
                                value={selectedTimeslot || ''}
                                onChange={ev => {
                                    const newTimeslotValue = ev.target.value;
                                    const selected = timeslotsForSelectedDate.find(
                                        t => t.timeslotValue === newTimeslotValue
                                    );
                                    if (selected) {
                                        setSelectedTimeslot(newTimeslotValue);
                                        // Calculate end time: use end_time from timeslot if available, otherwise add 1 hour
                                        const endTime = selected.end_time
                                            ? selected.end_time
                                            : moment(selected.start_time)
                                                  .add(1, 'hour')
                                                  .toISOString();
                                        setTimeshareTimeslot({
                                            start: selected.start_time,
                                            end: endTime,
                                            isCustom: false
                                        });
                                    }
                                }}
                                input={<Input />}
                                label="Available start times"
                            >
                                {timeslotsForSelectedDate.map(e => {
                                    let seats = '';
                                    if (e.limited_tickets) {
                                        if (e.seats_sold > e.seats_available) {
                                            seats = ` (overbooked by ${e.seats_sold -
                                                e.seats_available})`;
                                        } else if (e.seats_sold === e.seats_available) {
                                            seats = ' (sold out)';
                                        } else {
                                            seats = ` (${e.seats_available - e.seats_sold} left)`;
                                        }
                                    }
                                    return (
                                        <MenuItem key={e.timeslotValue} value={e.timeslotValue}>
                                            {moment(e.start_time).format('h:mm:ss A')} {seats}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    ) : (
                        <small>
                            No available start times on this date. Use custom time instead.
                        </small>
                    )}
                </Grid>
            )}

            {isCustomTime && (
                <>
                    <Grid item xs={6}>
                        <TextField
                            label="Start time"
                            type="time"
                            value={customTime}
                            onChange={handleCustomTimeChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                step: 1 // 1 second intervals for iCal sync
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="End time"
                            type="time"
                            value={customEndTime}
                            onChange={handleCustomEndTimeChange}
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputLabelProps={{
                                shrink: true
                            }}
                            inputProps={{
                                step: 1 // 1 second intervals for iCal sync
                            }}
                        />
                    </Grid>
                </>
            )}
            <Grid item xs={12}>
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                        setTimeshareTimeslot(null);
                        setSelectedTimeslot('');
                        setIsCustomTime(false);
                    }}
                    style={{ textAlign: 'center', cursor: 'pointer' }}
                >
                    Clear timeslot
                </Button>
            </Grid>
        </>
    );
}
